// covered-by: frontends/carrie/src/ask-v2/rail/__tests__/checkProgressRows.test.ts
import {
  type AskPunttCheckProgress,
  type AskPunttMessage,
} from "@mg/schemas/src/christo/askPuntt";

import { type AskV2RailProgressItem } from "./types";

/**
 * The row id prefix, so a check row can never collide with a `plan-` row.
 */
const CHECK_ROW_PREFIX = "check";

/**
 * warhol's one verifier (`agents/artwork/evidence.py`). It is the roster name
 * an accepted or rejected verdict comes back under, which is what the
 * "Verified k of n" line counts.
 */
export const ASK_V2_CHECK_VERIFIER_NAME = "evidence-verifier";

/**
 * The rail status a warhol check status draws as.
 *
 * `failed` and `timed_out` both mean the check did not do the work, and both
 * must reach the rail as a failure — a green tick on a timed-out reviewer is
 * the exact lie `P0-askv2-rail-ticks-from-write-plan` removed the timers for.
 *
 * `skipped` draws as `done` with the reason beside it, the treatment the plan
 * builder already gives a `cancelled` step: a check that did not run is a thing
 * that happened to the review and hiding it would make the row count disagree
 * with the fan-out. It is NOT drawn as a failure, because a skip is not one.
 */
export function railStatusForCheck(
  status: AskPunttCheckProgress["status"],
): AskV2RailProgressItem["status"] {
  switch (status) {
    case "done":
      return "done";
    case "failed":
    case "timed_out":
      return "failed";
    default:
      return "done";
  }
}

/**
 * One rail row per check the running turn has settled, in finish order.
 *
 * The ids are index-qualified because a name can repeat within a turn: six
 * reviewers are six distinct names, but a fan-out the model retries settles the
 * same name twice, and two rows keyed alike would collapse in React.
 */
export function checkProgressRows(
  checkProgress: readonly AskPunttCheckProgress[] | undefined,
): AskV2RailProgressItem[] {
  return (checkProgress ?? []).map((entry, index) => ({
    id: `${CHECK_ROW_PREFIX}-${index}-${entry.name}`,
    label: entry.name,
    ...(entry.reason ? { detail: entry.reason } : {}),
    status: railStatusForCheck(entry.status),
  }));
}

/**
 * How many of this turn's verifiers have finished, or null when none has run.
 *
 * `total` counts the verifier legs warhol has PUBLISHED, not the intents the
 * turn means to verify: warhol publishes a leg only when it settles, so a
 * verifier still running is not on the list and could not be counted, and
 * every published leg that is not a failure is `done`. The ratio is therefore
 * a running tally of settled work, never a completion bar — `total` grows with
 * `verified`. The copy says "so far" for exactly that reason: without it a
 * six-verifier turn reads "Verified 6 of 6" while five are still running,
 * which claims a review is finished when it is not.
 *
 * A true denominator needs warhol to publish how many verifiers a turn will
 * run. It does not today, and inventing one here is worse than a numerator
 * that rises.
 */
export function verifierTally(
  checkProgress: readonly AskPunttCheckProgress[] | undefined,
): { verified: number; total: number } | null {
  const verifiers = (checkProgress ?? []).filter(
    (entry) => entry.name === ASK_V2_CHECK_VERIFIER_NAME,
  );
  if (verifiers.length === 0) return null;
  return {
    verified: verifiers.filter((entry) => entry.status === "done").length,
    total: verifiers.length,
  };
}

/**
 * The checks the newest message in a review cycle carries, or nothing.
 *
 * Read off the LAST assistant message that has any, rather than off the last
 * message: the placeholder row is replaced in place when the answer lands and
 * the answer carries no `checkProgress`, so a cycle that has just finished has
 * none to give — but a cycle still waiting does, on its pending row.
 */
export function checkProgressFromMessages(
  messages: readonly AskPunttMessage[] | undefined,
): AskPunttCheckProgress[] {
  for (let index = (messages?.length ?? 0) - 1; index >= 0; index -= 1) {
    const entries = messages?.[index]?.checkProgress;
    if (entries?.length) return entries;
  }
  return [];
}

/**
 * The same rows, with the running turn's settled checks nested under the row
 * it is working on.
 *
 * Nests under the `active` row, or under the last row when nothing is active —
 * a plan whose steps are all `pending` while checks settle is a plan the model
 * has not advanced yet, and the checks still belong on the rail. An empty list
 * returns `items` untouched, so a turn with nothing settled is byte-identical
 * to what this rail drew before checks existed.
 */
export function nestCheckProgress(
  items: readonly AskV2RailProgressItem[],
  checkProgress: readonly AskPunttCheckProgress[] | undefined,
  verifiedDetail: (tally: { verified: number; total: number }) => string,
): AskV2RailProgressItem[] {
  const children = checkProgressRows(checkProgress);
  if (children.length === 0 || items.length === 0) return [...items];
  const activeIndex = items.findIndex((item) => item.status === "active");
  const isActiveRow = activeIndex >= 0;
  const target = isActiveRow ? activeIndex : items.length - 1;
  const tally = verifierTally(checkProgress);
  // The tally is the ACTIVE row's line, and only when the row has none of its
  // own. Falling back to the last row put it — and the checks under it — on a
  // step the model had withdrawn, and overwrote that row's own `skipped`
  // marker with a count of work it never did.
  const detail = isActiveRow && tally ? verifiedDetail(tally) : undefined;
  return items.map((item, index) =>
    index === target
      ? {
          ...item,
          ...(detail && !item.detail ? { detail } : {}),
          children,
        }
      : item,
  );
}
